import React, { useRef, useState, FormEvent } from 'react';
import { navigate } from 'gatsby';

import PostHogHelper from '~/utils/posthog-helper';
import { blindPostRequest } from '~/utils/network';

interface HopStartBlock {
  _template: string;
  css_class_name?: string;
  form_style: string;
  image?: string;
  image_alt_text?: string;
  style_class?: string;
  submit_label: string;
  support_text: string;
}

const HopStartFormWrapper = ({ block }: { block: HopStartBlock }) => {
  let contactUrl = 'https://api.bunny.net/hopstart/submit';
  /** Set by getWrappedForm  */
  let thankyouPageUrl = 'https://bunny.net/thank-you';

  const randomIdPrefix =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);

  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const formRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState('US');

  function splitWords(str: string) {
    // collapse whitespace and split; handles spaces/newlines/tabs
    const t = str.trim();
    return t ? t.split(/\s+/) : [];
  }

  function trimToWords(str: string, max: number) {
    const words = splitWords(str);
    if (words.length <= max) return str;
    return words.slice(0, max).join(' ');
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const contactEmail = `${formData.get('Email')}`;

    // Convert FormData to plain object
    const formBody: Record<string, any> = {};
    for (const [key, value] of formData.entries()) {
      formBody[key] = value;
    }

    blindPostRequest(contactUrl, formBody).then(async responseOk => {
      setSubmitted(true);

      if (responseOk) {
        setSuccess(true);
        await PostHogHelper.promiseToCaptureEmail(contactEmail);
        navigate(thankyouPageUrl);
      } else {
        setSuccess(false);
      }
    });
  };

  // CSS classes
  if (block.form_style === 'Centered') {
    block.style_class = ' style-centered';
  } else {
    block.style_class = ' style-basic';
  }

  return (
    <section
      className={`block ${block._template} ${block.css_class_name ? block.css_class_name : ''} contact-form ${block.style_class ? block.style_class : ''}`}
    >
      <div className='container'>
        {block.image && (
          <img
            src={block.image}
            alt={block.image_alt_text}
            title={block.image_alt_text}
            className='bunny-no-touch'
          />
        )}
        <div className='row'>
          <div className='form-cols'>
            <div className='form-wr'>
              <form
                onSubmit={handleSubmit}
                ref={formRef}
              >
                {submitted && !success && (
                  <div className='error-message'>
                    There was an error submitting this contact form. Please try
                    again or send us an email at support@bunny.net.
                  </div>
                )}
                <div className={`row${success ? 'is-hidden' : ''}`}>
                  <>
                    <div className='col-md-6'>
                      <div className='form-item'>
                        <label htmlFor={`${randomIdPrefix}-FullName`}>
                          Full Name
                        </label>
                        <input
                          id={`${randomIdPrefix}-FullName`}
                          type='text'
                          name='FullName'
                          required
                          placeholder='Bunny Hopkins'
                        />
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='form-item'>
                        <label htmlFor={`${randomIdPrefix}-Email`}>Email</label>
                        <input
                          id={`${randomIdPrefix}-Email`}
                          type='email'
                          name='Email'
                          required
                          placeholder='joe@company.com'
                        />
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='form-item'>
                        <label htmlFor={`${randomIdPrefix}-Company`}>
                          Company
                        </label>
                        <input
                          id={`${randomIdPrefix}-Company`}
                          type='text'
                          name='Company'
                          placeholder='Bunny Inc.'
                        />
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='form-item'>
                        <label htmlFor={`${randomIdPrefix}-Role`}>Role</label>
                        <input
                          id={`${randomIdPrefix}-Role`}
                          type='text'
                          name='Role'
                          required
                          placeholder='Head of Fluffle'
                        />
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='form-item'>
                        <label htmlFor={`${randomIdPrefix}-YearFounded`}>
                          Year Founded
                        </label>
                        <input
                          id={`${randomIdPrefix}-YearFounded`}
                          type='number'
                          name='YearFounded'
                          min='1900'
                          max='2100'
                          required
                          placeholder='2024'
                        />
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='form-item'>
                        <label htmlFor={`${randomIdPrefix}-CompanyWebsite`}>
                          Company Website (optional)
                        </label>
                        <input
                          id={`${randomIdPrefix}-CompanyWebsite`}
                          type='text'
                          name='CompanyWebsite'
                          placeholder='www.company.com'
                        />
                      </div>
                    </div>
                    <div className='col-12'>
                      <div className='form-item'>
                        <label htmlFor={`${randomIdPrefix}-ProjectDescription`}>
                          Describe your project, focusing on the impact you
                          expect to make for your users or industry (200 words
                          max.):
                        </label>
                        <textarea
                          id={`${randomIdPrefix}-ProjectDescription`}
                          name='ProjectDescription'
                          required
                          minLength={10}
                          maxLength={2000}
                          placeholder='Focus on the difference your product will make for your users or your industry. '
                        ></textarea>
                      </div>
                    </div>
                    <div className='col-12'>
                      <div className='form-item'>
                        <label
                          htmlFor={`${randomIdPrefix}-ExpectedProductsUsage`}
                        >
                          Tell us which bunny.net products you expect to use and
                          how (200 words max.):
                        </label>
                        <textarea
                          id={`${randomIdPrefix}-ExpectedProductsUsage`}
                          name='ExpectedProductsUsage'
                          required
                          minLength={10}
                          maxLength={2000}
                          placeholder='Tell us which bunny.net products you expect to use and how they’ll help you achieve your goals.'
                        ></textarea>
                      </div>
                    </div>
                  </>
                  <div className='col-12 form-submit'>
                    <button
                      type='submit'
                      name='submit'
                      className='form-action'
                      value={block.submit_label}
                    >
                      {block.submit_label}
                    </button>
                    <div
                      className='support-text'
                      dangerouslySetInnerHTML={{ __html: block.support_text }}
                    ></div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HopStartFormWrapper;
